import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import LiveClassRoutineHost from './live/LiveClassRoutineHost';
import RoutineTemplateEditor from './routines/RoutineTemplateEditor';
import { emptyLiveClassRoutine, emptyRoutine, routineFromApi } from '../shared/routineTemplateConstants';
import D28dSchedulingCalendar from './d28d/D28dSchedulingCalendar';
import D28dRoutineLibraryBox from './d28d/D28dRoutineLibraryBox';
import ScheduleDateTimePicker, { addMinutesToLocalValue } from './ui/ScheduleDateTimePicker';
import SearchableSelect from './ui/SearchableSelect';
import { formatDateTimeLocal, toApiDateTime } from '../utils/dateTimeLocal';
import {
  clearDraftStorage,
  confirmDiscardChanges,
  draftStorageKey,
  formHasLiveClassDraft,
  loadDraft,
  saveDraft,
} from '../utils/formDraft';
import { useLiveClassesAdmin } from '../hooks/useLiveClassesAdmin';
import { liveClassTypeOptions, mergeLiveClassTypeOptions } from '../constants/liveClassTypes';
import './LiveClasses.css';

const defaultForm = (gymScoped = false) => ({
  title: '',
  description: '',
  zoom_link: '',
  start_time: '',
  end_time: '',
  gym_id: '',
  is_global: false,
  active: true,
  program_id: '',
  d28d_routine_id: '',
  use_routine: false,
  d28d_session_adjustments: '',
  d28d_host_user_id: '',
  zoom_account_id: 'virtual_d28d_1',
  auto_zoom: false,
  skip_zoom: gymScoped,
  class_type: 'METODO D28D',
});

function routineOptionLabel(r) {
  const sub = r.subcategoria || r.nombre;
  return `${r.nombre} — ${r.categoria} — ${sub} (v ${r.version})`;
}

const TZ_CO = 'America/Bogota';

function formatClassDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-CO', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: TZ_CO,
    }).format(d);
  } catch {
    return String(value);
  }
}

/** Día calendario Colombia (YYYY-MM-DD) para filtrar listados. */
function classDateKeyCo(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ_CO,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
}

/** YYYY-MM-DD hoy en Colombia. */
function todayKeyCo() {
  return classDateKeyCo(new Date().toISOString());
}

/** Lunes (YYYY-MM-DD) de la semana que contiene `ymd` (Colombia). */
function mondayKeyCoFromYmd(ymd) {
  const key = String(ymd || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return todayKeyCo();
  const [y, m, d] = key.split('-').map(Number);
  // Mediodía Colombia = 17:00 UTC (UTC−5 fijo).
  const noon = new Date(Date.UTC(y, m - 1, d, 17, 0, 0));
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: TZ_CO, weekday: 'short' }).format(noon);
  const offset = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }[wd] ?? 0;
  const monday = new Date(Date.UTC(y, m - 1, d - offset, 17, 0, 0));
  return classDateKeyCo(monday.toISOString());
}

function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + Number(days || 0), 17, 0, 0));
  return classDateKeyCo(dt.toISOString());
}

function weekBoundsFromOffset(weekOffset = 0) {
  const monday = addDaysYmd(mondayKeyCoFromYmd(todayKeyCo()), Number(weekOffset || 0) * 7);
  return { from: monday, to: addDaysYmd(monday, 6) };
}

function formatYmdShortCo(ymd) {
  if (!ymd) return '—';
  try {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 17, 0, 0));
    return new Intl.DateTimeFormat('es-CO', {
      timeZone: TZ_CO,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(dt);
  } catch {
    return ymd;
  }
}

function resolveFormTitle(form, routines, gymScoped = false) {
  const custom = form.title?.trim();
  if (custom) return custom;
  if (form.use_routine && form.d28d_routine_id) {
    const r = routines.find((x) => String(x.id) === String(form.d28d_routine_id));
    if (r?.nombre) return r.nombre;
  }
  return gymScoped ? 'Sesión en vivo' : 'Sesión D28D';
}

function routineDraftToSnapshot(draft, routineId, routines) {
  const tpl = routines.find((r) => String(r.id) === String(routineId));
  return {
    id: Number(routineId),
    nombre: draft.nombre,
    categoria: draft.categoria,
    subcategoria: draft.subcategoria,
    objetivo: draft.objetivo,
    nivel: draft.nivel,
    duracion: draft.duracion,
    descripcion: draft.descripcion,
    notas_tecnicas: draft.notas_tecnicas,
    equipamiento: draft.equipamiento || [],
    version: tpl?.version ?? null,
    blocks: draft.blocks || [],
  };
}

function zoomModeLabel(zoom) {
  if (!zoom) return '';
  if (zoom.mode === 'api') return 'Zoom API (reunión nueva)';
  if (zoom.mode === 'program_fallback') return 'Enlace fijo del programa';
  if (zoom.mode === 'placeholder') return 'Enlace demo (configura ZOOM_S2S en servidor)';
  if (zoom.mode === 'pmi_fallback') return 'Enlace PMI fijo';
  return zoom.message || '';
}

function selectedProgramZoomEmail(form, programs) {
  const prog = programs.find((p) => p.id === form.program_id);
  if (!prog) return '';
  if (form.program_id === 'virtual_d28d' && Array.isArray(prog.zoom_accounts)) {
    const acc = prog.zoom_accounts.find((a) => a.id === form.zoom_account_id);
    return acc?.email || '';
  }
  return prog.zoom_email || '';
}

function formatGymPeopleList(people = []) {
  if (!people.length) return '—';
  return people.map((person) => person.nombre || person.email).join(', ');
}

function renderAttendanceGymCell(row) {
  const hasEnrolled = row.enrolled_by_gym?.length > 0;
  const hasAttended = row.by_gym?.length > 0;
  if (!hasEnrolled && !hasAttended) {
    return <span className="text-stone-500">Sin registros</span>;
  }
  return (
    <div className="space-y-3">
      {hasEnrolled ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-1">Inscritos</div>
          <div className="space-y-2">
            {row.enrolled_by_gym.map((gym) => (
              <div key={`${row.class_id}-en-${gym.gym_name}`} className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-3">
                <div className="font-semibold text-stone-900">{gym.gym_name} · {gym.count}</div>
                <div className="text-xs text-stone-600 mt-1">{formatGymPeopleList(gym.attendees)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {hasAttended ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-700 mb-1">Asistieron</div>
          <div className="space-y-2">
            {row.by_gym.map((gym) => (
              <div key={`${row.class_id}-at-${gym.gym_name}`} className="rounded-2xl bg-sky-50/60 border border-sky-100 p-3">
                <div className="font-semibold text-stone-900">{gym.gym_name} · {gym.count}</div>
                <div className="text-xs text-stone-600 mt-1">{formatGymPeopleList(gym.attendees)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function renderGymScopedAttendanceDetail(row) {
  const enrolledNames = formatGymPeopleList(row.enrolled_users);
  const attendedNames = formatGymPeopleList(
    (row.by_gym || []).flatMap((g) => g.attendees),
  );
  return (
  <div key={`detail-${row.class_id}`} className="rounded-2xl bg-stone-50 border border-slate-200 p-3">
    <div className="font-semibold text-stone-900">{row.title}</div>
    <div className="text-xs text-stone-600 mt-2 space-y-1">
      <div><span className="font-semibold text-emerald-800">Inscritos:</span> {enrolledNames}</div>
      <div><span className="font-semibold text-sky-800">Asistieron:</span> {attendedNames}</div>
    </div>
  </div>
  );
}

export default function AdminLiveClasses({
  embedded = false,
  scopeGymId = null,
  scopeGymName = '',
  attendanceOnly = false,
  onDirtyChange = null,
}) {
  const { user: currentUser } = useAuth();
  const { t } = useI18n();
  const isGymScoped = scopeGymId != null && scopeGymId !== '';
  const draftKey = draftStorageKey(scopeGymId || 'platform', 'live-class');
  const { items, loading: itemsLoading, fetchItems } = useLiveClassesAdmin(scopeGymId);
  const [form, setForm] = useState(() => defaultForm(isGymScoped));
  const [catalogClassTypes, setCatalogClassTypes] = useState(() => liveClassTypeOptions(isGymScoped));
  const classTypeOptions = useMemo(
    () => mergeLiveClassTypeOptions([
      ...catalogClassTypes,
      ...items.map((i) => i.class_type).filter(Boolean),
      form.class_type,
    ]),
    [catalogClassTypes, items, form.class_type],
  );
  const [formDirty, setFormDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [classTypeFilter, setClassTypeFilter] = useState('');
  const [coachFilter, setCoachFilter] = useState('');
  // Por defecto: semana actual (lun–dom Colombia). «Ver todas» limpia el rango.
  const [dateFromFilter, setDateFromFilter] = useState(() => weekBoundsFromOffset(0).from);
  const [dateToFilter, setDateToFilter] = useState(() => weekBoundsFromOffset(0).to);
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 25;
  const [templateWeekOffset, setTemplateWeekOffset] = useState(0);
  const [templateFrom, setTemplateFrom] = useState('');
  const [templateTo, setTemplateTo] = useState('');
  const [lastCreatedId, setLastCreatedId] = useState(null);
  const [adminMode, setAdminMode] = useState('list');
  const [attendance, setAttendance] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [d28dHosts, setD28dHosts] = useState([]);
  const [previewClass, setPreviewClass] = useState(null);
  const [classRoutineDraft, setClassRoutineDraft] = useState(null);
  const [routineEditorMode, setRoutineEditorMode] = useState(null);
  const [editingRoutineId, setEditingRoutineId] = useState(null);
  const [routineEditForm, setRoutineEditForm] = useState(emptyRoutine());
  const [routineCategories, setRoutineCategories] = useState([]);
  const [routineEditSaving, setRoutineEditSaving] = useState(false);
  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [importingExcel, setImportingExcel] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const formRef = useRef(null);

  const canEditRoutines = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles) && currentUser.roles.length
      ? currentUser.roles
      : [currentUser?.rol].filter(Boolean);
    if (roles.some((r) => ['super_admin', 'admin_d28d'].includes(r))) return true;
    if (isGymScoped && roles.some((r) => ['admin_marca', 'admin_gimnasio', 'admin_gym', 'entrenador_gym'].includes(r))) {
      return true;
    }
    return false;
  }, [currentUser, isGymScoped]);

  const routineRequestParams = useMemo(() => (
    isGymScoped && scopeGymId ? { gym_id: scopeGymId, gym_only: 'true' } : {}
  ), [isGymScoped, scopeGymId]);

  const applyGymFormDefaults = useCallback((base) => {
    if (!isGymScoped) return base;
    let next = { ...base };
    if (!next.program_id && programs.length) {
      const defaultProg = programs.find((p) => p.active !== false) || programs[0];
      if (defaultProg) next.program_id = defaultProg.id;
    }
    if (!next.d28d_host_user_id && d28dHosts.length === 1) {
      next.d28d_host_user_id = String(d28dHosts[0].id);
    } else if (!next.d28d_host_user_id && currentUser?.id) {
      const me = String(currentUser.id);
      if (d28dHosts.some((h) => String(h.id) === me)) {
        next.d28d_host_user_id = me;
      }
    }
    if (scopeGymId) {
      next.gym_id = String(scopeGymId);
      next.is_global = false;
    }
    return next;
  }, [isGymScoped, programs, d28dHosts, currentUser?.id, scopeGymId]);

  const clearFormDraft = useCallback(() => {
    clearDraftStorage(draftKey);
    setFormDirty(false);
    onDirtyChange?.(false);
  }, [draftKey, onDirtyChange]);

  const guardUnsaved = useCallback((action) => {
    if (!formDirty && !formHasLiveClassDraft(form)) {
      action();
      return;
    }
    if (confirmDiscardChanges()) {
      clearFormDraft();
      action();
    }
  }, [formDirty, form, clearFormDraft]);

  const fetchAttendance = async () => {
    try {
      const resp = await api.get('/live-classes/admin/attendance');
      setAttendance(resp.data?.data || []);
    } catch (err) {
      console.warn('No se pudo cargar reporte de asistencia', err);
    }
  };

  const fetchPrograms = async () => {
    try {
      const params = isGymScoped && scopeGymId
        ? { gym_id: scopeGymId, gym_only: 'true' }
        : { platform_only: 'true' };
      const resp = await api.get('/programs', { params });
      setPrograms(resp.data?.data || []);
    } catch (err) {
      console.warn('Error loading programs', err);
    }
  };

  const fetchRoutines = useCallback(async () => {
    try {
      let resp;
      const params = { ...routineRequestParams };
      try {
        resp = await api.get('/d28d/routines/schedule', { params });
      } catch {
        resp = await api.get('/d28d/routines', { params: { estado: 'activa', ...params } });
      }
      setRoutines(resp.data?.data || []);
      setLibraryRefresh((n) => n + 1);
    } catch {
      setRoutines([]);
    }
  }, [routineRequestParams]);

  const isPlatformScheduler = useMemo(() => {
    const roles = Array.isArray(currentUser?.roles) && currentUser.roles.length
      ? currentUser.roles
      : [currentUser?.rol].filter(Boolean);
    return roles.some((r) => ['super_admin', 'admin_d28d'].includes(r));
  }, [currentUser]);

  const fetchD28dHosts = useCallback(async () => {
    try {
      const params = {};
      if (scopeGymId) params.gym_id = scopeGymId;
      const resp = await api.get('/live-classes/admin/d28d-hosts', { params });
      setD28dHosts(resp.data?.data || []);
    } catch {
      setD28dHosts([]);
    }
  }, [scopeGymId]);

  const fetchClassTypes = useCallback(async () => {
    try {
      const resp = await api.get('/live-classes/admin/class-types');
      const list = resp.data?.data;
      if (Array.isArray(list) && list.length) {
        setCatalogClassTypes(mergeLiveClassTypeOptions(list));
      }
    } catch {
      /* keep defaults */
    }
  }, []);

  const addClassTypeFromUi = async () => {
    const name = window.prompt('Nombre del nuevo tipo de clase:');
    if (!name || !String(name).trim()) return;
    try {
      const resp = await api.post('/live-classes/admin/class-types', { name: String(name).trim() });
      const list = resp.data?.data;
      if (Array.isArray(list)) {
        setCatalogClassTypes(mergeLiveClassTypeOptions(list));
      } else {
        await fetchClassTypes();
      }
      handleChange('class_type', String(name).trim());
      setSuccess(`Tipo de clase «${String(name).trim()}» agregado.`);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo crear el tipo de clase');
    }
  };

  const fetchRoutineCategories = async () => {
    try {
      const params = isGymScoped && scopeGymId
        ? { gym_id: scopeGymId, gym_only: 'true' }
        : {};
      const resp = await api.get('/d28d/routines/categories', { params });
      setRoutineCategories((resp.data?.data || []).map((c) => c.nombre));
    } catch {
      setRoutineCategories([]);
    }
  };

  const loadClassRoutineDraft = async (routineId, existingSnapshot = null) => {
    if (!routineId) {
      setClassRoutineDraft(null);
      return;
    }
    if (existingSnapshot?.blocks?.length) {
      setClassRoutineDraft(routineFromApi(existingSnapshot));
      return;
    }
    try {
      const resp = await api.get(`/d28d/routines/${routineId}`);
      const data = resp.data?.data;
      setClassRoutineDraft(data ? routineFromApi(data) : emptyRoutine());
    } catch {
      setClassRoutineDraft(emptyRoutine());
    }
  };

  useEffect(() => {
    if (attendanceOnly) {
      fetchAttendance();
      return;
    }
    fetchAttendance();
    fetchPrograms();
    fetchRoutines();
    fetchD28dHosts();
    fetchClassTypes();
    fetchRoutineCategories();
  }, [attendanceOnly, fetchRoutines, fetchD28dHosts, fetchClassTypes]);

  useEffect(() => {
    if (attendanceOnly) return;
    const saved = loadDraft(draftKey);
    if (saved && !saved.id && formHasLiveClassDraft(saved)) {
      setForm(applyGymFormDefaults({ ...defaultForm(isGymScoped), ...saved }));
    }
  }, [attendanceOnly, draftKey, isGymScoped, applyGymFormDefaults]);

  useEffect(() => {
    if (attendanceOnly) return;
    const dirty = formHasLiveClassDraft(form);
    setFormDirty(dirty);
    onDirtyChange?.(dirty);
    if (dirty) saveDraft(draftKey, form);
    else clearDraftStorage(draftKey);
  }, [form, draftKey, attendanceOnly, onDirtyChange]);

  useEffect(() => {
    if (!scopeGymId) return;
    setForm((prev) => (
      prev.gym_id && String(prev.gym_id) === String(scopeGymId)
        ? prev
        : { ...prev, gym_id: String(scopeGymId), is_global: false }
    ));
  }, [scopeGymId]);

  useEffect(() => {
    if (!isGymScoped || !programs.length) return;
    setForm((prev) => {
      const next = applyGymFormDefaults(prev);
      if (
        next.program_id === prev.program_id
        && next.d28d_host_user_id === prev.d28d_host_user_id
      ) {
        return prev;
      }
      return next;
    });
  }, [isGymScoped, programs, d28dHosts, applyGymFormDefaults]);

  const openEditRoutineEditor = async (routineId) => {
    if (!routineId || !canEditRoutines) return;
    try {
      const resp = await api.get(`/d28d/routines/${routineId}`);
      const data = resp.data?.data;
      if (!data) return;
      setEditingRoutineId(routineId);
      setRoutineEditForm(routineFromApi(data));
      setRoutineEditorMode('edit');
    } catch {
      setError('No se pudo cargar la rutina para editar.');
    }
  };

  const openCreateRoutineEditor = () => {
    if (!canEditRoutines) return;
    setEditingRoutineId(null);
    setRoutineEditForm(emptyLiveClassRoutine());
    setRoutineEditorMode('create');
  };

  const closeRoutineEditor = () => {
    setRoutineEditorMode(null);
    setEditingRoutineId(null);
  };

  const importRoutineCatalog = async () => {
    if (!canEditRoutines || !window.confirm(t('live.admin.confirm_import_catalog', '¿Importar plantillas D28D del catálogo base? (omite duplicados)'))) return;
    try {
      setError('');
      const res = await api.post('/d28d/routines/import/bundled');
      const r = res.data?.data || {};
      setSuccess(`Importación: ${r.created || 0} creadas, ${r.skipped || 0} omitidas.`);
      await fetchRoutines();
    } catch (e) {
      setError(e.response?.data?.message || 'Error importando catálogo.');
    }
  };

  const saveRoutineTemplate = async () => {
    try {
      setRoutineEditSaving(true);
      setError('');
      const body = { ...routineEditForm, scope: isGymScoped ? 'gym_wl' : 'd28d_platform' };
      if (isGymScoped && scopeGymId) body.gym_id = Number(scopeGymId);
      if (routineEditorMode === 'create') {
        const res = await api.post('/d28d/routines', body);
        const newId = res.data?.data?.id;
        await fetchRoutines();
        closeRoutineEditor();
        if (newId) {
          handleChange('use_routine', true);
          await onRoutineSelected(newId);
        }
        setSuccess('Rutina creada. Ya puedes usarla en la clase.');
        return;
      }
      if (!editingRoutineId) return;
      await api.put(`/d28d/routines/${editingRoutineId}`, { ...body, new_version: false });
      await fetchRoutines();
      if (String(form.d28d_routine_id) === String(editingRoutineId)) {
        await loadClassRoutineDraft(editingRoutineId);
      }
      closeRoutineEditor();
      setSuccess('Plantilla de rutina guardada.');
    } catch (e) {
      setError(e.response?.data?.message || 'Error guardando la rutina.');
    } finally {
      setRoutineEditSaving(false);
    }
  };

  const handleSelectRoutineForClass = async (routine) => {
    handleChange('use_routine', true);
    await onRoutineSelected(routine.id);
  };

  const categoryOptions = useMemo(() => {
    const names = [...routineCategories];
    [routineEditForm.categoria, classRoutineDraft?.categoria].forEach((c) => {
      if (c && !names.includes(c)) names.push(c);
    });
    return names;
  }, [routineCategories, routineEditForm.categoria, classRoutineDraft?.categoria]);

  const filteredItems = useMemo(() => {
    let list = items;
    const q = classSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const prog = programs.find((p) => p.id === item.program_id)?.name || '';
        const haystack = [item.title, item.coach, prog, item.zoom_link, item.description, item.class_type]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    if (classTypeFilter) {
      list = list.filter((item) => String(item.class_type || 'METODO D28D').toUpperCase().includes(classTypeFilter.split(' ')[0]));
    }
    if (coachFilter) {
      if (coachFilter.startsWith('name:')) {
        const name = coachFilter.slice(5);
        list = list.filter((item) => String(item.coach || '').trim() === name);
      } else {
        const host = d28dHosts.find((h) => String(h.id) === String(coachFilter));
        list = list.filter((item) => String(item.d28d_host_user_id || '') === String(coachFilter)
          || (host && String(item.coach || '').trim().toLowerCase() === String(host.nombre || '').trim().toLowerCase()));
      }
    }
    const from = String(dateFromFilter || '').trim();
    const to = String(dateToFilter || '').trim();
    if (from || to) {
      list = list.filter((item) => {
        const day = classDateKeyCo(item.start_time);
        if (!day) return false;
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
      });
    }
    return list;
  }, [items, classSearch, programs, classTypeFilter, coachFilter, d28dHosts, dateFromFilter, dateToFilter]);

  const listTotalPages = Math.max(1, Math.ceil(filteredItems.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, listTotalPages);
  const pagedItems = useMemo(() => {
    const start = (safeListPage - 1) * LIST_PAGE_SIZE;
    return filteredItems.slice(start, start + LIST_PAGE_SIZE);
  }, [filteredItems, safeListPage, LIST_PAGE_SIZE]);

  useEffect(() => {
    setListPage(1);
  }, [classSearch, classTypeFilter, coachFilter, dateFromFilter, dateToFilter]);

  const applyWeekOffset = useCallback((offset) => {
    const { from, to } = weekBoundsFromOffset(offset);
    setDateFromFilter(from);
    setDateToFilter(to);
  }, []);

  const clearDateFilterToAll = useCallback(() => {
    setDateFromFilter('');
    setDateToFilter('');
  }, []);

  const coachFilterOptions = useMemo(() => {
    const options = [{ value: '', label: 'Todos' }];
    const seen = new Set();
    d28dHosts.forEach((h) => {
      const label = `${h.nombre} (${h.email})`;
      options.push({ value: String(h.id), label });
      seen.add(String(h.nombre || '').trim().toLowerCase());
    });
    items.forEach((item) => {
      if (item.d28d_host_user_id) return;
      const label = String(item.coach || '').trim();
      if (!label || seen.has(label.toLowerCase())) return;
      seen.add(label.toLowerCase());
      options.push({ value: `name:${label}`, label });
    });
    return options;
  }, [items, d28dHosts]);

  const hostLabel = (hostId) => {
    const h = d28dHosts.find((x) => String(x.id) === String(hostId));
    return h ? `${h.nombre} (${h.email})` : '—';
  };

  const routineOptions = useMemo(
    () => routines.map((r) => ({
      value: r.id,
      label: r.nombre,
      hint: `${r.categoria}${r.subcategoria ? ` · ${r.subcategoria}` : ''} · v${r.version}`,
    })),
    [routines],
  );

  const programOptions = useMemo(
    () => programs.map((p) => ({ value: p.id, label: p.name })),
    [programs],
  );

  const hostOptions = useMemo(
    () => d28dHosts.map((h) => ({ value: String(h.id), label: h.nombre, hint: h.email })),
    [d28dHosts],
  );

  const onRoutineSelected = async (routineId) => {
    const r = routines.find((x) => String(x.id) === String(routineId));
    setForm((prev) => ({
      ...prev,
      d28d_routine_id: routineId,
      title: prev.title.trim() ? prev.title : (r?.nombre || prev.title),
    }));
    setClassRoutineDraft(emptyRoutine());
    await loadClassRoutineDraft(routineId);
  };

  const onUseRoutineChange = (checked) => {
    if (!checked) {
      setClassRoutineDraft(null);
      setForm((prev) => ({
        ...prev,
        use_routine: false,
        d28d_routine_id: '',
        d28d_session_adjustments: '',
      }));
      return;
    }
    handleChange('use_routine', true);
  };

  const applySessionDuration = (minutes) => {
    if (!form.start_time) return;
    handleChange('end_time', addMinutesToLocalValue(form.start_time, minutes));
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const generateZoomLink = async () => {
    if (!form.program_id || !form.start_time || !form.end_time) {
      setError('Selecciona programa, inicio y fin antes de generar Zoom.');
      return;
    }
    const sessionTitle = resolveFormTitle(form, routines, isGymScoped);
    const selectedProg = programs.find((p) => p.id === form.program_id);
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const resp = await api.post('/live-classes/admin/zoom-meeting', {
        title: sessionTitle,
        program_id: form.program_id,
        gym_id: isGymScoped && scopeGymId ? scopeGymId : (form.gym_id || null),
        zoom_account_id: form.program_id === 'virtual_d28d'
          ? form.zoom_account_id
          : (selectedProg?.zoom_account_id || null),
        start_time: toApiDateTime(form.start_time),
        end_time: toApiDateTime(form.end_time),
        d28d_host_user_id: form.d28d_host_user_id ? Number(form.d28d_host_user_id) : null,
        d28d_routine_id: form.use_routine && form.d28d_routine_id ? Number(form.d28d_routine_id) : null,
        auto_zoom: true,
      });
      const link = resp.data?.data?.zoom_link;
      const zoomInfo = resp.data?.data?.zoom;
      if (!link) {
        setError(resp.data?.error || 'No se recibió enlace Zoom. Verifica programa y fechas.');
        return;
      }
      setForm((prev) => ({ ...prev, zoom_link: link, auto_zoom: true }));
      const mode = zoomModeLabel(zoomInfo);
      const sala = zoomInfo?.host_email ? ` · Sala Zoom: ${zoomInfo.host_email}` : '';
      const anfitrion = zoomInfo?.alternative_host
        ? ` · Anfitrión D28D (alterno): ${zoomInfo.alternative_host}`
        : form.d28d_host_user_id
          ? ' · Asigna entrenador D28D para anfitrión alterno en Zoom'
          : '';
      setSuccess(`✓ Zoom generado${mode ? ` (${mode})` : ''}${sala}${anfitrion}: ${link}`);
    } catch (e) {
      const data = e.response?.data || {};
      const z = data.zoom;
      const detail = data.error || data.message || z?.message || 'No se pudo generar el enlace Zoom.';
      const extra = z?.host_email
        ? ` Cuenta del programa: ${z.host_email}${z.alternative_host ? `; anfitrión alterno: ${z.alternative_host}` : ''}.`
        : '';
      const hint = detail.includes('S2S') || detail.includes('PMI')
        ? ' Configura API Zoom S2S en backend/.env o un enlace fijo PMI en Maestros → Programas → Zoom.'
        : '';
      setError(detail + extra + hint);
    } finally {
      setSaving(false);
    }
  };

  const templateRangeLabel = useMemo(() => {
    if (templateFrom && templateTo) {
      return `${formatYmdShortCo(templateFrom)} → ${formatYmdShortCo(templateTo)}`;
    }
    const bounds = weekBoundsFromOffset(templateWeekOffset);
    const weekTag = templateWeekOffset === 0
      ? 'esta semana'
      : templateWeekOffset === -1
        ? 'semana anterior'
        : templateWeekOffset === 1
          ? 'semana siguiente'
          : `semana ${templateWeekOffset > 0 ? '+' : ''}${templateWeekOffset}`;
    return `${formatYmdShortCo(bounds.from)} → ${formatYmdShortCo(bounds.to)} (${weekTag})`;
  }, [templateFrom, templateTo, templateWeekOffset]);

  const downloadLiveClassesTemplate = async () => {
    try {
      const params = scopeGymId ? { gym_id: scopeGymId } : {};
      if (templateFrom && templateTo) {
        params.from = templateFrom;
        params.to = templateTo;
      } else {
        params.week_offset = templateWeekOffset;
      }
      const res = await api.get('/live-classes/admin/import-template', {
        responseType: 'blob',
        params,
      });
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/i.exec(cd);
      const filename = match?.[1] || 'plantilla_clases_en_vivo.xlsx';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      const rowsHdr = res.headers?.['x-template-rows'];
      const rowsNote = rowsHdr != null ? ` · ${rowsHdr} clase(s)` : '';
      setSuccess(
        `Plantilla descargada: ${templateRangeLabel}${rowsNote}. `
        + 'Edita ENTRENADOR/HOST_EMAIL e importa con «Actualizar coincidencias».',
      );
      // Alinea el filtro del listado al mismo rango para revisar hosts.
      if (templateFrom && templateTo) {
        setDateFromFilter(templateFrom);
        setDateToFilter(templateTo);
      } else {
        const bounds = weekBoundsFromOffset(templateWeekOffset);
        setDateFromFilter(bounds.from);
        setDateToFilter(bounds.to);
      }
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response?.data instanceof Blob
          ? null
          : err.message);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          setError(parsed.error || 'No se pudo descargar la plantilla Excel de clases en vivo');
          return;
        } catch { /* fallthrough */ }
      }
      setError(msg || 'No se pudo descargar la plantilla Excel de clases en vivo');
    }
  };

  const runImportLiveClassesExcel = async (
    file,
    mode = 'merge',
    { createMissingClassTypes = false, hostOverrides = null } = {},
  ) => {
    setImportingExcel(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      // Campos texto antes del archivo; mode también en query por si el multipart omite campos.
      formData.append('mode', mode);
      if (scopeGymId) formData.append('gym_id', String(scopeGymId));
      if (createMissingClassTypes) formData.append('create_missing_class_types', 'true');
      if (hostOverrides && Object.keys(hostOverrides).length) {
        formData.append('host_overrides', JSON.stringify(hostOverrides));
      }
      formData.append('file', file);
      const res = await api.post('/live-classes/admin/import', formData, {
        params: { mode },
      });
      const report = res.data?.data;
      if (Array.isArray(report?.class_types) && report.class_types.length) {
        setCatalogClassTypes(mergeLiveClassTypeOptions(report.class_types));
      } else {
        await fetchClassTypes();
      }
      const warnCount = report?.warnings?.length ?? 0;
      const errList = Array.isArray(report?.errors) ? report.errors : [];
      const trainerFixable = errList.filter(
        (e) => e.code === 'unknown_trainer' && Array.isArray(e.suggestions) && e.suggestions.length,
      );
      if (trainerFixable.length) {
        const nextOverrides = { ...(hostOverrides || {}) };
        let accepted = 0;
        for (const e of trainerFixable) {
          if (e.row == null || nextOverrides[String(e.row)]) continue;
          const suggested = e.suggestions[0];
          const others = e.suggestions.slice(1, 6).join(', ');
          const title = e.title ? ` «${e.title}»` : '';
          const coach = e.coach ? ` (Excel: «${e.coach}»)` : '';
          const ok = window.confirm(
            `Fila ${e.row}${title}${coach}: el entrenador no coincide.\n\n`
            + `¿Reemplazar por «${suggested}» y reintentar esa fila?`
            + (others ? `\nOtras opciones: ${others}` : ''),
          );
          if (ok) {
            nextOverrides[String(e.row)] = suggested;
            accepted += 1;
          }
        }
        if (accepted > 0) {
          await runImportLiveClassesExcel(file, mode, {
            createMissingClassTypes: true,
            hostOverrides: nextOverrides,
          });
          return;
        }
      }
      const appliedMode = report?.mode || mode;
      const modeLabel = appliedMode === 'append'
        ? 'adición'
        : appliedMode === 'replace_period'
          ? 'reemplazo de periodo'
          : 'actualización';
      let summary = `Importación (${modeLabel}): ${report?.rows_unique ?? report?.found ?? 0} filas únicas`
        + ` · ${report?.created ?? 0} creadas, ${report?.updated ?? 0} actualizadas`
        + (report?.skipped ? `, ${report.skipped} omitidas (ya existían)` : '')
        + (appliedMode === 'replace_period' || report?.deleted_period
          ? `, ${report?.deleted_period ?? 0} eliminadas del periodo`
          : '')
        + (report?.removed_duplicates ? `, ${report.removed_duplicates} duplicados BD` : '')
        + (report?.duplicates_in_file ? `, ${report.duplicates_in_file} duplicadas en Excel` : '')
        + `, ${report?.failed ?? 0} con error`
        + (warnCount ? `, ${warnCount} con aviso.` : '.')
        + ` Hora Colombia (${report?.timezone || 'America/Bogota'}).`;
      if (errList.length) {
        const detail = errList.slice(0, 20).map((e) => {
          const rowLabel = e.row != null ? `Fila ${e.row}` : 'Fila ?';
          const title = e.title ? ` «${e.title}»` : '';
          const note = e.suggestions?.length
            ? ` Sugerencias: ${e.suggestions.slice(0, 8).join(', ')}.`
            : '';
          return `• ${rowLabel}${title}: ${e.message || 'Error'}${note}`;
        }).join('\n');
        const more = errList.length > 20 ? `\n… y ${errList.length - 20} más.` : '';
        summary += `\n\nDetalle de errores:\n${detail}${more}`;
        setError(summary);
        setSuccess('');
      } else {
        setSuccess(summary);
      }
      await fetchItems();
      await fetchAttendance();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'unknown_class_types' && Array.isArray(data.unknown_types) && data.unknown_types.length) {
        const known = Array.isArray(data.known_types) && data.known_types.length
          ? `\nTipos actuales: ${data.known_types.join(', ')}.`
          : '';
        const ok = window.confirm(
          `Hay tipos de clase que no existen en el sistema:\n• ${data.unknown_types.join('\n• ')}\n\n`
          + `¿Deseas crearlos, agregarlos al catálogo y continuar con la importación?${known}`,
        );
        if (ok) {
          await runImportLiveClassesExcel(file, mode, {
            createMissingClassTypes: true,
            hostOverrides,
          });
          return;
        }
        setError(
          `Importación cancelada: tipos desconocidos (${data.unknown_types.join(', ')}). `
          + 'Corrige TIPO_CLASE en el Excel o confírmalos para crearlos.',
        );
        return;
      }
      if (data.code === 'replace_precheck_failed' && Array.isArray(data.errors) && data.errors.length) {
        const trainerFixable = data.errors.filter(
          (e) => e.code === 'unknown_trainer' && Array.isArray(e.suggestions) && e.suggestions.length,
        );
        if (trainerFixable.length) {
          const nextOverrides = { ...(hostOverrides || {}) };
          let accepted = 0;
          for (const e of trainerFixable) {
            if (e.row == null || nextOverrides[String(e.row)]) continue;
            const suggested = e.suggestions[0];
            const others = e.suggestions.slice(1, 6).join(', ');
            const title = e.title ? ` «${e.title}»` : '';
            const coach = e.coach ? ` (Excel: «${e.coach}»)` : '';
            const ok = window.confirm(
              `Reemplazar periodo pausado — Fila ${e.row}${title}${coach}: el entrenador no coincide.\n\n`
              + `¿Usar «${suggested}» y reintentar? (no se borró la programación)`
              + (others ? `\nOtras opciones: ${others}` : ''),
            );
            if (ok) {
              nextOverrides[String(e.row)] = suggested;
              accepted += 1;
            }
          }
          if (accepted > 0) {
            await runImportLiveClassesExcel(file, mode, {
              createMissingClassTypes: true,
              hostOverrides: nextOverrides,
            });
            return;
          }
        }
        const detail = data.errors.slice(0, 20).map((e) => {
          const rowLabel = e.row != null ? `Fila ${e.row}` : 'Fila ?';
          const title = e.title ? ` «${e.title}»` : '';
          return `• ${rowLabel}${title}: ${e.message || 'Error'}`;
        }).join('\n');
        setError(`${data.error || 'Reemplazar periodo cancelado'}\n${detail}`);
        return;
      }
      const msg = data.error || err.message;
      const errList = Array.isArray(data.errors) ? data.errors : [];
      if (errList.length) {
        const detail = errList.slice(0, 15).map((e) => {
          const rowLabel = e.row != null ? `Fila ${e.row}` : '';
          return `• ${rowLabel} ${e.message || ''}`.trim();
        }).join('\n');
        setError(`${msg || 'Error importando'}\n${detail}`);
      } else {
        setError(msg || 'Error importando clases desde Excel');
      }
    } finally {
      setImportingExcel(false);
      setImportPreview(null);
      setPendingImportFile(null);
    }
  };

  const handleImportLiveClassesExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingExcel(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      if (scopeGymId) formData.append('gym_id', String(scopeGymId));
      formData.append('file', file);
      const res = await api.post('/live-classes/admin/import/preview', formData);
      const preview = res.data?.data;
      setPendingImportFile(file);
      if (preview?.needs_choice) {
        setImportPreview(preview);
        setImportingExcel(false);
        return;
      }
      await runImportLiveClassesExcel(file, 'merge');
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg || 'Error analizando el archivo Excel');
      setPendingImportFile(null);
      setImportPreview(null);
      setImportingExcel(false);
    }
  };

  const confirmImportMode = async (mode) => {
    if (!pendingImportFile) return;
    await runImportLiveClassesExcel(pendingImportFile, mode);
  };

  const cancelImportPreview = () => {
    setImportPreview(null);
    setPendingImportFile(null);
    setImportingExcel(false);
  };

  const formatPreviewRange = (range) => {
    if (!range?.minStart || !range?.maxEnd) return '—';
    return `${formatClassDateTime(range.minStart)} → ${formatClassDateTime(range.maxEnd)}`;
  };

  const handleSubmit = async (event) => {
      event.preventDefault();
      const sessionTitle = resolveFormTitle(form, routines, isGymScoped);
      if (!sessionTitle && !(form.use_routine && form.d28d_routine_id)) {
        setError(isGymScoped ? 'Indica el nombre de sesión o selecciona una rutina.' : 'Indica el nombre de sesión o selecciona una rutina D28D.');
        return;
      }
      if (!form.program_id) {
        setError(isGymScoped ? 'Selecciona el programa de tu gimnasio.' : 'Selecciona el programa D28D (necesario para Zoom automático).');
        return;
      }
      if (isGymScoped && !form.d28d_host_user_id) {
        setError('Selecciona el entrenador que dictará la clase en tu gimnasio.');
        return;
      }
      const startApi = toApiDateTime(form.start_time);
      if (startApi && new Date(startApi).getTime() < Date.now()) {
        setError('No puedes programar clases en fechas u horarios pasados.');
        return;
      }
      try {
        setSaving(true);
        setError('');
        setSuccess('');
        const selectedProg = programs.find((p) => p.id === form.program_id);
        const payload = {
          title: sessionTitle || undefined,
          description: form.description,
          zoom_link: form.skip_zoom ? '' : (form.auto_zoom && !form.zoom_link ? '' : form.zoom_link),
          start_time: toApiDateTime(form.start_time),
          end_time: toApiDateTime(form.end_time),
          gym_id: isGymScoped && scopeGymId ? scopeGymId : (form.gym_id || null),
          is_global: form.is_global,
          active: form.active,
          program_id: form.program_id || null,
          zoom_account_id: form.program_id === 'virtual_d28d'
            ? form.zoom_account_id
            : (selectedProg?.zoom_account_id || null),
          auto_zoom: form.skip_zoom ? false : form.auto_zoom,
          skip_zoom: form.skip_zoom,
          d28d_routine_id: form.use_routine && form.d28d_routine_id ? Number(form.d28d_routine_id) : null,
          d28d_session_adjustments: form.use_routine ? (form.d28d_session_adjustments || '').trim() : null,
          d28d_host_user_id: form.d28d_host_user_id ? Number(form.d28d_host_user_id) : null,
          class_type: form.class_type || (isGymScoped ? 'FUERZA' : 'METODO D28D'),
        };
        if (form.use_routine && form.d28d_routine_id) {
          const draft = classRoutineDraft || emptyRoutine();
          payload.d28d_routine_snapshot = {
            ...routineDraftToSnapshot(draft, form.d28d_routine_id, routines),
            session_adjustments: (form.d28d_session_adjustments || '').trim() || null,
          };
        }
        if (form.id) {
          const resp = await api.put(`/live-classes/admin/${form.id}`, payload);
          setSuccess(resp.data?.message || 'Clase actualizada correctamente.');
          setLastCreatedId(form.id);
        } else {
          const resp = await api.post('/live-classes/admin', payload);
          const created = resp.data?.data;
          const zoomNote = resp.data?.zoom?.mode === 'placeholder'
            ? ' · Enlace demo (configura ZOOM_S2S_* en backend/.env para Zoom real)'
            : resp.data?.zoom?.mode === 'api'
              ? ' · Reunión Zoom creada por API'
              : '';
          const zoomWarning = resp.data?.zoom_warning;
          const noZoom = !created?.zoom_link;
          setSuccess(
            zoomWarning
              ? `✓ ${resp.data?.message || 'Clase creada'}. ${zoomWarning}`
              : noZoom
                ? `✓ ${resp.data?.message || 'Clase creada correctamente'}. Sin enlace Zoom: el entrenador puede publicarlo en Mi día.`
                : `${resp.data?.message || 'Clase creada correctamente'}. Zoom: ${created?.zoom_link}${zoomNote}`,
          );
          setLastCreatedId(created?.id ?? null);
        }
        setForm(applyGymFormDefaults(defaultForm(isGymScoped)));
        setClassRoutineDraft(null);
        clearFormDraft();
        await fetchItems();
        await fetchD28dHosts();
        await fetchAttendance();
      } catch (e) {
        const msg = e.response?.data?.error || e.message;
        setError(msg || 'Error guardando la clase.');
        setSuccess('');
      } finally {
        setSaving(false);
      }
    };

  const cancelEdit = () => {
    setForm(applyGymFormDefaults(defaultForm(isGymScoped)));
    setClassRoutineDraft(null);
    setPreviewClass(null);
    clearFormDraft();
    setError('');
    setSuccess('');
  };

  const handleEdit = (item) => {
    setAdminMode('list');
    setError('');
    setSuccess(`Modo edición: «${item.title}». El formulario está arriba — revisa Zoom y pulsa «Actualizar clase».`);
    setForm({
      id: item.id,
      title: item.title,
      description: item.description,
      zoom_link: item.zoom_link || '',
      start_time: formatDateTimeLocal(item.start_time),
      end_time: formatDateTimeLocal(item.end_time),
      gym_id: item.gym_id || '',
      is_global: !!item.is_global,
      active: !!item.active,
      program_id: item.program_id || '',
      d28d_routine_id: item.d28d_routine_id || '',
      use_routine: Boolean(item.d28d_routine_id),
      d28d_session_adjustments: item.d28d_routine_snapshot?.session_adjustments
        || item.d28d_session_adjustments
        || '',
      d28d_host_user_id: item.d28d_host_user_id ? String(item.d28d_host_user_id) : '',
      zoom_account_id: item.zoom_account_id || 'virtual_d28d_1',
      auto_zoom: false,
      skip_zoom: false,
      class_type: item.class_type || 'METODO D28D',
    });
    setPreviewClass(item);
    if (item.d28d_routine_id) {
      loadClassRoutineDraft(
        item.d28d_routine_id,
        item.d28d_routine_snapshot || item.d28d_routine || null,
      );
    } else {
      setClassRoutineDraft(null);
    }
    window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('live.admin.confirm_delete', '¿Eliminar esta clase en vivo?'))) return;
    try {
      setSaving(true);
      await api.delete(`/live-classes/admin/${item.id}`);
      await fetchItems();
      await fetchAttendance();
    } catch {
      setError(t('live.admin.delete_error', 'Error eliminando la clase.'));
    } finally {
      setSaving(false);
    }
  };

  if (attendanceOnly) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-stone-900">
              {isGymScoped
                ? `Asistencia${scopeGymName ? ` — ${scopeGymName}` : ''}`
                : 'Reporte de asistentes por gym'}
            </h3>
            <p className="text-sm text-stone-600">
              {isGymScoped
                ? 'Inscritos y asistentes de tu sede en cada clase programada.'
                : 'Inscritos y asistentes por clase, agrupados por gimnasio.'}
            </p>
          </div>
          <button type="button" className="btn-secondary" onClick={fetchAttendance}>
            Actualizar reporte
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left text-stone-500 text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-4 py-3">Clase</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Inscritos</th>
                <th className="px-4 py-3">Asistentes</th>
                {!isGymScoped ? <th className="px-4 py-3">Gimnasios</th> : null}
              </tr>
            </thead>
            <tbody>
              {attendance.length === 0 ? (
                <tr>
                  <td colSpan={isGymScoped ? 4 : 5} className="px-4 py-6 text-center text-stone-500">
                    Sin asistencia registrada todavía.
                  </td>
                </tr>
              ) : attendance.map((row) => (
                <tr key={row.class_id} className="border-t border-slate-200 align-top">
                  <td className="px-4 py-4 font-semibold text-stone-900">{row.title}</td>
                  <td className="px-4 py-4 text-stone-600 text-xs">{formatClassDateTime(row.start_time)}</td>
                  <td className="px-4 py-4 text-stone-900 font-semibold">{row.total_enrolled ?? 0}</td>
                  <td className="px-4 py-4 text-stone-900 font-semibold">{row.total_attendees}</td>
                  {!isGymScoped ? (
                    <td className="px-4 py-4">{renderAttendanceGymCell(row)}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isGymScoped && attendance.some((row) => (row.total_enrolled ?? 0) > 0 || row.total_attendees > 0) ? (
          <div className="px-5 py-4 border-t border-slate-200 space-y-3">
            {attendance
              .filter((row) => (row.total_enrolled ?? 0) > 0 || row.total_attendees > 0)
              .map((row) => renderGymScopedAttendanceDetail(row))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'card d28d-admin-surface surface-readable '}max-w-[88rem] mx-auto`}>
      <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${embedded ? 'mb-4' : 'mb-6'}`}>
        {!embedded ? (
          <div>
            <h2 className="text-3xl font-bold text-stone-900">Administrar Clases en Vivo</h2>
            <p className="text-sm text-stone-600 mt-2">Crea, edita y publica sesiones de Zoom para tus entrenamientos en vivo.</p>
          </div>
        ) : null}
        <div className={`live-classes-panel__subtabs flex gap-2 flex-wrap ${embedded ? '' : 'ml-auto'}`}>
          <button
            type="button"
            className={`live-classes-panel__tab ${adminMode === 'list' ? 'live-classes-panel__tab--active' : ''}`}
            onClick={() => guardUnsaved(() => setAdminMode('list'))}
          >
            Lista
          </button>
          <button
            type="button"
            className={`live-classes-panel__tab ${adminMode === 'calendar' ? 'live-classes-panel__tab--active' : ''}`}
            onClick={() => guardUnsaved(() => setAdminMode('calendar'))}
          >
            Calendario
          </button>
        </div>
      </div>

      {adminMode === 'calendar' && (
        <div className="mb-8 space-y-4 surface-readable rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-stone-600">
            Vista semanal/mensual para planificar. El calendario de inscripción de usuarios está en la pestaña
            {' '}
            <strong>Calendario</strong>
            {' '}
            del menú Clases en vivo.
          </p>
          <div className="flex flex-wrap gap-3">
            <label className="text-sm">
              <span className="font-semibold text-stone-800 block mb-1">Tipo de clase</span>
              <select className="ui-input" value={classTypeFilter} onChange={(e) => setClassTypeFilter(e.target.value)}>
                <option value="">Todos</option>
                {classTypeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="text-sm min-w-[12rem]">
              <span className="font-semibold text-stone-800 block mb-1">Entrenador</span>
              <select className="ui-input w-full" value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)}>
                {coachFilterOptions.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
          <D28dSchedulingCalendar
            programs={programs}
            items={items}
            loadingItems={itemsLoading}
            classTypeFilter={classTypeFilter}
            coachFilter={coachFilter}
            scopeGymId={scopeGymId}
            isGymScoped={isGymScoped}
            onEditClass={handleEdit}
            onRefetch={async () => {
              await fetchItems();
              await fetchAttendance();
            }}
          />
        </div>
      )}

      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 whitespace-pre-wrap text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-800 p-4 rounded-lg mb-4 whitespace-pre-wrap text-sm break-words">{success}</div>}

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-lg w-full max-h-[min(90vh,calc(100vh-2rem))] overflow-y-auto p-6 rounded-2xl border border-slate-200 shadow-2xl my-auto">
            <h3 className="text-xl font-bold text-stone-900 mb-2">Importar programación</h3>
            <p className="text-sm text-stone-600 mb-4">
              El archivo tiene <strong>{importPreview.rows_unique ?? importPreview.rows_in_file}</strong> clase(s) únicas
              {importPreview.duplicates_in_file
                ? ` (${importPreview.duplicates_in_file} fila(s) duplicada(s) en Excel se omitirán)`
                : ''}
              {' '}
              entre <strong>{formatPreviewRange(importPreview.date_range)}</strong> (hora Colombia).
            </p>
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              Ya hay <strong>{importPreview.existing_in_range}</strong> clase(s) programada(s) en ese periodo
              {importPreview.programs?.length
                ? ` (${importPreview.programs.join(', ')})`
                : ''}
              . ¿Qué deseas hacer?
            </p>
            <ul className="text-xs text-stone-600 space-y-1 mb-5 list-disc pl-4">
              <li><strong>Adicionar:</strong> solo crea clases nuevas; las que ya existen en el mismo horario se omiten.</li>
              <li><strong>Reemplazar periodo:</strong> borra las clases del rango del archivo y carga las {importPreview.rows_unique ?? importPreview.rows_in_file} del Excel.</li>
              <li><strong>Actualizar coincidencias:</strong> actualiza la clase del mismo horario (programa + título + inicio/fin), rellena o cambia el host (ENTRENADOR/HOST_EMAIL) sin borrar el periodo; si hay duplicados en ese slot, deja una. Crea solo las que no existan.</li>
            </ul>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary"
                disabled={importingExcel}
                onClick={() => confirmImportMode('append')}
              >
                {importingExcel ? 'Importando…' : 'Adicionar'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={importingExcel}
                onClick={() => confirmImportMode('replace_period')}
              >
                Reemplazar periodo
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={importingExcel}
                onClick={() => confirmImportMode('merge')}
              >
                Actualizar coincidencias
              </button>
              <button type="button" className="btn-secondary ml-auto" disabled={importingExcel} onClick={cancelImportPreview}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {routineEditorMode && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="d28d-admin-modal surface-readable border border-slate-200 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[min(90vh,calc(100vh-2rem))] overflow-y-auto p-6 my-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-stone-900">
                {routineEditorMode === 'create'
                  ? (isGymScoped ? 'Nueva rutina' : 'Nueva rutina D28D')
                  : (isGymScoped ? 'Editar plantilla' : 'Editar plantilla D28D')}
              </h3>
              <button type="button" className="btn-secondary" onClick={closeRoutineEditor}>Cerrar</button>
            </div>
            <RoutineTemplateEditor
              form={routineEditForm}
              setForm={setRoutineEditForm}
              categoryOptions={categoryOptions}
              readOnly={false}
              liveClass
            />
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <button type="button" className="btn-primary" disabled={routineEditSaving} onClick={saveRoutineTemplate}>
                {routineEditorMode === 'create' ? 'Crear rutina' : 'Guardar plantilla'}
              </button>
              <button type="button" className="btn-secondary" onClick={closeRoutineEditor}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {adminMode === 'list' ? (
      <>
      <D28dRoutineLibraryBox
        canEdit={canEditRoutines}
        selectedRoutineId={form.d28d_routine_id}
        refreshToken={libraryRefresh}
        gymScoped={isGymScoped}
        requestParams={routineRequestParams}
        onSelectForClass={handleSelectRoutineForClass}
        onEditRoutine={openEditRoutineEditor}
        onCreateRoutine={openCreateRoutineEditor}
        onImportCatalog={isGymScoped ? null : importRoutineCatalog}
      />
      <div className="mb-6 space-y-3 rounded-xl border border-slate-200 bg-white/80 p-4 surface-readable">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">Plantilla Excel</span>
          <span className="text-xs text-stone-600">{templateRangeLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => {
                setTemplateFrom('');
                setTemplateTo('');
                setTemplateWeekOffset((n) => n - 1);
              }}
            >
              ← Semana ant.
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => {
                setTemplateFrom('');
                setTemplateTo('');
                setTemplateWeekOffset(0);
              }}
            >
              Esta semana
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => {
                setTemplateFrom('');
                setTemplateTo('');
                setTemplateWeekOffset((n) => n + 1);
              }}
            >
              Semana sig. →
            </button>
          </div>
          <label className="text-xs text-stone-700">
            Desde
            <input
              type="date"
              className="input mt-1 block text-sm"
              value={templateFrom}
              onChange={(e) => {
                setTemplateFrom(e.target.value);
                if (e.target.value && templateTo && e.target.value > templateTo) {
                  setTemplateTo(e.target.value);
                }
              }}
            />
          </label>
          <label className="text-xs text-stone-700">
            Hasta
            <input
              type="date"
              className="input mt-1 block text-sm"
              value={templateTo}
              min={templateFrom || undefined}
              onChange={(e) => setTemplateTo(e.target.value)}
            />
          </label>
          {(templateFrom || templateTo) ? (
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => {
                setTemplateFrom('');
                setTemplateTo('');
              }}
            >
              Usar selector de semana
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={downloadLiveClassesTemplate}
            disabled={Boolean((templateFrom && !templateTo) || (!templateFrom && templateTo))}
          >
            Descargar plantilla Excel
          </button>
          <label className="btn-secondary text-sm cursor-pointer">
            {importingExcel ? 'Importando…' : 'Importar programación (.xlsx)'}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              disabled={importingExcel}
              onChange={handleImportLiveClassesExcel}
            />
          </label>
          <span className="text-xs text-stone-600 max-w-xl">
            Descarga la semana/rango, edita ENTRENADOR o HOST_EMAIL e importa con{' '}
            <strong>Actualizar coincidencias</strong>. Hora Colombia (America/Bogotá).
          </span>
        </div>
      </div>
      <form ref={formRef} className="d28d-schedule-form surface-readable mb-8 space-y-5 rounded-xl border border-slate-200 p-4" onSubmit={handleSubmit}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {form.id ? `Editar clase #${form.id}` : 'Programar clase'}
            </h3>
            <p className="text-sm text-slate-600">
              {form.use_routine && form.d28d_routine_id
                ? 'Tres columnas: sesión, Zoom y rutina editable solo para esta clase.'
                : (isGymScoped
                  ? 'Completa sesión y Zoom. Activa «Usar rutina» para editar la rutina de la clase.'
                  : 'Completa sesión y Zoom. Activa «Usar rutina D28D» para editar la rutina de la clase.')}
            </p>
          </div>
          <label className="ui-check-row inline-flex">
            <input
              type="checkbox"
              checked={form.use_routine}
              onChange={(e) => onUseRoutineChange(e.target.checked)}
            />
            <span>{isGymScoped ? 'Usar rutina' : 'Usar rutina D28D'}</span>
          </label>
        </div>

        {form.id ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-blue-900">
              Editando <strong>{form.title || 'clase sin título'}</strong>
              {' '}
              — los cambios se guardan con «Actualizar clase».
            </p>
            <button type="button" className="btn-secondary text-sm" onClick={cancelEdit}>
              Cancelar edición
            </button>
          </div>
        ) : null}

        {form.use_routine && (
          <div className="space-y-3 rounded-xl border border-lime-200 bg-lime-50/30 p-4">
            <SearchableSelect
              label={isGymScoped ? 'Rutina (plantilla)' : 'Rutina D28D (plantilla base)'}
              value={form.d28d_routine_id}
              onChange={onRoutineSelected}
              options={routineOptions}
              placeholder="Buscar por nombre, categoría o método…"
              emptyLabel="No hay rutinas activas con ese texto"
              required
            />
            <div className="flex flex-wrap gap-2">
              {form.d28d_routine_id && canEditRoutines && (
                <button type="button" className="btn-secondary text-sm" onClick={() => openEditRoutineEditor(form.d28d_routine_id)}>
                  Editar plantilla maestra
                </button>
              )}
              {canEditRoutines && (
                <button type="button" className="btn-secondary text-sm" onClick={openCreateRoutineEditor}>
                  Nueva rutina
                </button>
              )}
              {form.d28d_routine_id && (
                <span className="text-xs text-slate-600 self-center">
                  {routineOptions.find((o) => String(o.value) === String(form.d28d_routine_id))?.hint}
                  {' · '}
                  Los cambios en la tercera columna no alteran la plantilla.
                </span>
              )}
            </div>
          </div>
        )}

        <div className={`grid gap-5 ${form.use_routine && form.d28d_routine_id ? 'd28d-schedule-form__grid--three' : 'lg:grid-cols-2'}`}>
          {/* Columna izquierda — sesión */}
          <section className="ui-light-panel space-y-4">
            <label className="block">
              <span className="ui-field__label">Nombre de sesión</span>
              <input
                className="ui-input"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required={!form.use_routine || !form.d28d_routine_id}
                placeholder={form.use_routine ? 'Se autocompleta con la rutina (editable)' : (isGymScoped ? 'Ej: Fuerza - Lunes 08:00' : 'Ej: FUERZA D28D - Lunes 08:00')}
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="ui-field__label">Tipo de clase</span>
                <div className="flex gap-2 items-stretch">
                  <select className="ui-input" value={form.class_type} onChange={(e) => handleChange('class_type', e.target.value)}>
                    {classTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-secondary shrink-0"
                    title="Agregar tipo de clase"
                    onClick={addClassTypeFromUi}
                  >
                    +
                  </button>
                </div>
              </label>
              <SearchableSelect
                label={isGymScoped ? 'Programa' : 'Programa D28D'}
                value={form.program_id}
                onChange={(pid) => {
                  handleChange('program_id', pid);
                  if (pid === 'virtual_d28d') handleChange('zoom_account_id', 'virtual_d28d_1');
                }}
                options={programOptions}
                placeholder="Buscar programa…"
                required
              />
            </div>

            {form.program_id && selectedProgramZoomEmail(form, programs) ? (
              <p className="text-xs text-slate-600 -mt-1">
                Zoom maestro: <strong>{selectedProgramZoomEmail(form, programs)}</strong>
              </p>
            ) : null}

            <SearchableSelect
              label={isGymScoped ? 'Entrenador de la clase (host)' : 'Entrenador D28D (host)'}
              value={form.d28d_host_user_id}
              onChange={(v) => handleChange('d28d_host_user_id', v)}
              options={hostOptions}
              placeholder={d28dHosts.length
                ? 'Buscar entrenador…'
                : (isGymScoped ? 'No hay entrenadores en este gym' : 'No hay entrenadores D28D registrados')}
              required={isGymScoped}
            />
            {isGymScoped && form.d28d_host_user_id ? (
              <p className="ui-field__hint">
                El entrenador seleccionado será el host de la sesión en Zoom.
              </p>
            ) : null}
            {isGymScoped && d28dHosts.length === 0 ? (
              <p className="ui-field__hint text-amber-700">
                Crea entrenadores en Maestros → Entrenadores para poder asignarlos.
              </p>
            ) : null}

            {isGymScoped && scopeGymName ? (
              <p className="ui-field__hint">
                Gimnasio: <strong>{scopeGymName}</strong>
              </p>
            ) : null}

            <ScheduleDateTimePicker
              label="Inicio"
              value={form.start_time}
              onChange={(v) => handleChange('start_time', v)}
              required
              id="sched-class-start"
              durationPresets={[45, 60, 75, 90]}
              onDurationPreset={applySessionDuration}
              durationPresetsDisabled={!form.start_time}
            />

            <ScheduleDateTimePicker
              label="Fin de sesión"
              value={form.end_time}
              onChange={(v) => handleChange('end_time', v)}
              required
              id="sched-class-end"
            />

            <label className="block">
              <span className="ui-field__label">Descripción</span>
              <textarea
                className="ui-input min-h-[88px]"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Notas visibles para el usuario (opcional)"
              />
            </label>
          </section>

          {/* Columna derecha — Zoom, host, rutina */}
          <section className="ui-light-panel space-y-4">
            {form.program_id === 'virtual_d28d' && (
              <label className="block">
                <span className="ui-field__label">Cuenta Zoom Virtual</span>
                <select
                  className="ui-input"
                  value={form.zoom_account_id}
                  onChange={(e) => handleChange('zoom_account_id', e.target.value)}
                >
                  <option value="virtual_d28d_1">Cuenta 1 — D28dzoom1@gmail.com</option>
                  <option value="virtual_d28d_2">Cuenta 2 — d28dzoom2@gmail.com</option>
                </select>
              </label>
            )}

            <div className="ui-light-panel ui-light-panel--muted space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="ui-field__label mb-0">Enlace Zoom</span>
                <label className="ui-check-row py-2 px-3 border-0 bg-transparent">
                  <input
                    type="checkbox"
                    checked={!!form.skip_zoom}
                    onChange={(e) => handleChange('skip_zoom', e.target.checked)}
                  />
                  <span>Sin enlace por ahora</span>
                </label>
              </div>
              {form.skip_zoom ? (
                <p className="ui-field__hint">
                  La clase se guardará sin Zoom. El entrenador asignado podrá pegar el enlace en <strong>Mi día</strong> para que los alumnos entren.
                </p>
              ) : (
                <>
                  <label className="ui-check-row py-2 px-3 border-0 bg-transparent">
                    <input
                      type="checkbox"
                      checked={!!form.auto_zoom}
                      onChange={(e) => handleChange('auto_zoom', e.target.checked)}
                    />
                    <span>Auto-generar con API Zoom</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <input
                      className="ui-input flex-1 min-w-[180px]"
                      value={form.zoom_link}
                      onChange={(e) => handleChange('zoom_link', e.target.value)}
                      readOnly={form.auto_zoom && !form.zoom_link}
                      placeholder={form.auto_zoom ? 'Pulsa Generar Zoom o guarda la clase' : 'https://zoom.us/j/… (opcional)'}
                    />
                    <button
                      type="button"
                      className="btn-primary text-sm whitespace-nowrap"
                      disabled={saving || !form.program_id || !form.start_time || !form.end_time || form.auto_zoom === false}
                      onClick={generateZoomLink}
                    >
                      {saving ? 'Generando…' : 'Generar Zoom'}
                    </button>
                  </div>
                  {form.zoom_link ? (
                    <p className="ui-field__hint text-green-700 break-all">Enlace listo: {form.zoom_link}</p>
                  ) : (
                    <p className="ui-field__hint">
                      Pega un enlace manualmente, auto-genera con API Zoom, o marca «Sin enlace por ahora».
                    </p>
                  )}
                </>
              )}
            </div>

            {form.use_routine && (
              <p className="ui-field__hint">
                El entrenador asignado arriba será anfitrión alterno en Zoom para esta sesión.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 pt-1">
              <label className="ui-check-row">
                <input type="checkbox" checked={form.is_global} onChange={(e) => handleChange('is_global', e.target.checked)} />
                <span>Global</span>
              </label>
              <label className="ui-check-row">
                <input type="checkbox" checked={form.active} onChange={(e) => handleChange('active', e.target.checked)} />
                <span>Activa</span>
              </label>
            </div>

            {!isGymScoped && (
              <label className="block">
                <span className="ui-field__label">Gym ID (opcional)</span>
                <input
                  className="ui-input"
                  value={form.gym_id}
                  onChange={(e) => handleChange('gym_id', e.target.value)}
                  placeholder="Solo marca blanca"
                />
              </label>
            )}
          </section>

          {form.use_routine && form.d28d_routine_id && (
            <section className="ui-light-panel space-y-3 d28d-schedule-form__routine-col">
              <div>
                <h4 className="text-base font-bold text-slate-900">Rutina para esta clase</h4>
                <p className="text-xs text-slate-600 mt-1">
                  Edita bloques y ejercicios aquí. Al guardar la clase se conserva una copia (snapshot) sin cambiar la plantilla.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white max-h-[min(70vh,720px)] overflow-y-auto p-3 d28d-admin-surface surface-readable">
                <RoutineTemplateEditor
                  form={classRoutineDraft || emptyRoutine()}
                  setForm={setClassRoutineDraft}
                  categoryOptions={categoryOptions}
                  readOnly={!classRoutineDraft}
                  liveClass
                />
              </div>
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Ajustes puntuales de sesión</span>
                <textarea
                  className="ui-input w-full min-h-[72px] mt-1"
                  value={form.d28d_session_adjustments}
                  onChange={(e) => handleChange('d28d_session_adjustments', e.target.value)}
                  placeholder="Ej: reducir carga 10%, sustituir burpees…"
                />
              </label>
            </section>
          )}
        </div>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-200">
          <button type="submit" className="btn-primary" disabled={saving}>
            {form.id ? 'Actualizar clase' : 'Crear clase'}
          </button>
          {form.use_routine && !form.d28d_routine_id && (
            <p className="text-sm text-amber-700 self-center">Selecciona una rutina para continuar.</p>
          )}
        </div>
      </form>

      {previewClass?.d28d_routine && (
        <LiveClassRoutineHost classItem={previewClass} user={null} />
      )}

      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <span className="font-semibold text-slate-700 block mb-1">Tipo de clase</span>
            <select className="input" value={classTypeFilter} onChange={(e) => setClassTypeFilter(e.target.value)}>
              <option value="">Todos</option>
              {classTypeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
          <label className="text-sm min-w-[12rem]">
            <span className="font-semibold text-slate-700 block mb-1">Entrenador</span>
            <select className="input w-full" value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)}>
              {coachFilterOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="font-semibold text-slate-700 block mb-1">Desde (CO)</span>
            <input
              type="date"
              className="input"
              value={dateFromFilter}
              max={dateToFilter || undefined}
              onChange={(e) => setDateFromFilter(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="font-semibold text-slate-700 block mb-1">Hasta (CO)</span>
            <input
              type="date"
              className="input"
              value={dateToFilter}
              min={dateFromFilter || undefined}
              onChange={(e) => setDateToFilter(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2 items-center pb-0.5">
            <button type="button" className="btn-secondary text-sm" onClick={() => applyWeekOffset(-1)}>
              Semana ant.
            </button>
            <button type="button" className="btn-primary text-sm" onClick={() => applyWeekOffset(0)}>
              Semana actual
            </button>
            <button type="button" className="btn-secondary text-sm" onClick={() => applyWeekOffset(1)}>
              Semana sig.
            </button>
            {(dateFromFilter || dateToFilter) ? (
              <button type="button" className="btn-secondary text-sm" onClick={clearDateFilterToAll}>
                Ver todas
              </button>
            ) : (
              <button type="button" className="btn-secondary text-sm" onClick={() => applyWeekOffset(0)}>
                Volver a semana
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <label className="block flex-1 max-w-md">
          <span className="text-sm font-semibold text-slate-700">Buscar clases</span>
          <input
            type="search"
            className="input w-full mt-1"
            placeholder="Título, coach, programa, Zoom…"
            value={classSearch}
            onChange={(e) => setClassSearch(e.target.value)}
          />
        </label>
        <p className="text-xs text-slate-500">
          {filteredItems.length} de {items.length}
          {(dateFromFilter || dateToFilter)
            ? ` · rango ${dateFromFilter || '…'} → ${dateToFilter || '…'} (Colombia)`
            : ' · todas las fechas (consulta ampliada)'}
          {filteredItems.length > LIST_PAGE_SIZE
            ? ` · pág. ${safeListPage}/${listTotalPages} (${LIST_PAGE_SIZE}/pág.)`
            : ''}
          {' · '}orden por horario (Colombia)
        </p>
        </div>
      </div>

      <div className="live-admin-table-wrap">
        <table className="live-admin-table">
          <thead>
            <tr>
              <th>Nombre sesión</th>
              <th>Zoom</th>
              <th>{isGymScoped ? 'Rutina' : 'Rutina D28D'}</th>
              <th>{isGymScoped ? 'Entrenador' : 'Host D28D'}</th>
              <th>Fecha y hora (CO)</th>
              <th>Programa</th>
              <th>Gym / Global</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="live-admin-table__empty">
                  Cargando clases...
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan="9" className="live-admin-table__empty">
                  {(dateFromFilter || dateToFilter)
                    ? 'Ninguna clase en este rango. Amplía fechas o usa «Ver todas».'
                    : 'Ninguna clase coincide con la búsqueda.'}
                </td>
              </tr>
            ) : (
              pagedItems.map((item) => (
                <tr
                  key={item.id}
                  className={lastCreatedId === item.id ? 'live-admin-table__row--highlight' : undefined}
                >
                  <td>
                    <span className="live-admin-table__title">{item.title}</span>
                  </td>
                  <td className="live-admin-table__muted" title={item.zoom_link}>
                    {item.zoom_link ? (
                      <a href={item.zoom_link} target="_blank" rel="noopener noreferrer" className="live-admin-table__link">
                        Ver enlace
                      </a>
                    ) : (
                      <span className="live-admin-table__warn">Pendiente · Mi día</span>
                    )}
                  </td>
                  <td className="live-admin-table__muted">
                    {item.d28d_routine?.nombre || item.d28d_routine_snapshot?.nombre || '—'}
                  </td>
                  <td className="live-admin-table__muted">
                    {hostLabel(item.d28d_host_user_id) || item.coach || '—'}
                  </td>
                  <td className="live-admin-table__muted live-admin-table__nowrap">
                    <div>{formatClassDateTime(item.start_time)}</div>
                    <div className="live-admin-table__sub">→ {formatClassDateTime(item.end_time)}</div>
                  </td>
                  <td>
                    <span className="live-admin-table__program">
                      {programs.find(p => p.id === item.program_id)?.name || '---'}
                    </span>
                  </td>
                  <td className="live-admin-table__muted">{item.is_global ? 'Global' : item.gym_id || 'Privada'}</td>
                  <td>
                    <span className={`live-admin-badge ${item.active ? 'live-admin-badge--on' : 'live-admin-badge--off'}`}>
                      {item.active ? 'Sí' : 'No'}
                    </span>
                  </td>
                  <td>
                    <div className="live-admin-actions">
                      <button
                        type="button"
                        className="live-admin-btn live-admin-btn--edit"
                        onClick={() => handleEdit(item)}
                      >
                        <Pencil className="w-3.5 h-3.5" aria-hidden />
                        Editar
                      </button>
                      <button
                        type="button"
                        className="live-admin-btn live-admin-btn--danger"
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden />
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {filteredItems.length > LIST_PAGE_SIZE ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            Mostrando {(safeListPage - 1) * LIST_PAGE_SIZE + 1}
            –
            {Math.min(safeListPage * LIST_PAGE_SIZE, filteredItems.length)}
            {' '}de {filteredItems.length} en el filtro actual
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={safeListPage <= 1}
              onClick={() => setListPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span className="text-sm text-slate-600 self-center px-1">
              {safeListPage} / {listTotalPages}
            </span>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={safeListPage >= listTotalPages}
              onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
            >
              Siguiente
            </button>
          </div>
        </div>
      ) : null}

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-stone-900">Reporte de asistentes por gym</h3>
            <p className="text-sm text-stone-600">Inscritos y asistentes por clase, agrupados por gimnasio.</p>
          </div>
          <button type="button" className="btn-secondary" onClick={fetchAttendance}>Actualizar reporte</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left text-stone-500 text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-4 py-3">Clase</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Inscritos</th>
                <th className="px-4 py-3">Asistentes</th>
                <th className="px-4 py-3">Gimnasios</th>
              </tr>
            </thead>
            <tbody>
              {attendance.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-6 text-center text-stone-500">Sin asistencia registrada todavía.</td>
                </tr>
              ) : attendance.map((row) => (
                <tr key={row.class_id} className="border-t border-slate-200 align-top">
                  <td className="px-4 py-4 font-semibold text-stone-900">{row.title}</td>
                  <td className="px-4 py-4 text-stone-600 text-xs">{formatClassDateTime(row.start_time)}</td>
                  <td className="px-4 py-4 text-stone-900 font-semibold">{row.total_enrolled ?? 0}</td>
                  <td className="px-4 py-4 text-stone-900 font-semibold">{row.total_attendees}</td>
                  <td className="px-4 py-4">{renderAttendanceGymCell(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </>
      ) : null}
    </div>
  );
}
